import {
    AdminBooking,
    AdminBookingTotals,
    AdminInfluencer,
    ApiResponse,
    approveAdminBookingToBill,
    BookingStatus,
    confirmAdminBooking,
    declineAdminBooking,
    getAdminBooking,
    getAdminBookings,
    getAllAdminInfluencers,
    patchAdminBooking,
    submitAdminBookingStatus,
    CreateAndSendAdminInvoiceData,
    createAndSendAdminInvoice,
    PatchAdminBookingData,
} from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import { mapApiErrorsToFormikErrors } from '../../../utils/utits';
import { showNotification } from '../../../utils/showNotification';
import debounce from 'lodash/debounce';
import uniq from 'lodash/uniq';

type OperationResult = { success: boolean; reason?: string };

export type UseAdminBookingsResult = {
    bookings: AdminBooking[];
    totals: AdminBookingTotals | null;
    influencers: AdminInfluencer[];
    isInitialized: boolean;
    isLoading: boolean;
    hasErrorLoadingData: boolean;
    hasNextPage: boolean;

    init: (requestInit?: RequestInit) => Promise<void>;
    reset: () => void;
    loadNextPage: (requestInit?: RequestInit) => Promise<void>;
    setStatus: (bookingId: number, status: BookingStatus) => Promise<OperationResult>;
    approveToBill: (
        bookingId: number,
        data: {
            create_invoice: boolean;
            discount?: number;
        }
    ) => Promise<OperationResult>;
    createAndSendInvoice: (data: CreateAndSendAdminInvoiceData) => Promise<OperationResult>;
    addBooking: (bookingId: number) => Promise<OperationResult>;
    editBooking: (bookingId: number, data: PatchAdminBookingData) => Promise<OperationResult>;
};

type UseAdminBookingsParams = Pick<
    Parameters<typeof getAdminBookings>[0],
    | 'page_size'
    | 'excluded_statuses'
    | 'booking_manager_id'
    | 'campaign_id'
    | 'influencer_id'
    | 'client_id'
    | 'booker_id'
    | 'date_created_start'
    | 'date_created_end'
    | 'date_approved_start'
    | 'date_approved_end'
>;

export function useAdminBookings({
    page_size,
    excluded_statuses,
    booking_manager_id,
    campaign_id,
    influencer_id,
    client_id,
    booker_id,
    date_created_start,
    date_created_end,
    date_approved_start,
    date_approved_end,
}: UseAdminBookingsParams): UseAdminBookingsResult {
    const [page, setPage] = useState(1);

    const [bookings, setBookings] = useState<AdminBooking[]>([]);
    const [totals, setTotals] = useState<AdminBookingTotals | null>(null);
    const [influencers, setInfluencers] = useState<AdminInfluencer[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoadingData, setHasErrorLoadingData] = useState(false);

    const fetchInfluencers = useCallback(async (...params: Parameters<typeof getAllAdminInfluencers>) => {
        try {
            const influencers = await getAllAdminInfluencers(...params);
            setInfluencers((prev) => prev.concat(influencers));
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification('Error loading influencers', 'error');
        }
    }, []);

    const fetchBookingsData = useCallback(
        async (...params: Parameters<typeof getAdminBookings>) => {
            try {
                setIsLoading(true);
                setHasErrorLoadingData(false);
                const response = await getAdminBookings(...params);
                if (response.status === 200) {
                    setBookings((bookings) => bookings.concat(response.data.results));
                    setTotals(response.data.totals);
                    setHasNextPage(Boolean(response.data.next));

                    const influencerIds = uniq(
                        response.data.results.map((booking) => booking.influencer_id).filter((id): id is number => !!id)
                    );

                    const influencerIdsToFetch = influencerIds.filter(
                        (influencerId) => !influencers.map((inf) => inf.id).includes(influencerId)
                    );

                    if (influencerIdsToFetch.length) {
                        await fetchInfluencers(influencerIdsToFetch.join(','), params[1]);
                    }
                    setIsInitialized(true);
                    return;
                }

                setHasErrorLoadingData(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoadingData(true);
                throw e;
            } finally {
                setIsLoading(false);
            }
        },
        [fetchInfluencers, influencers]
    );

    const init = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchBookingsData(
                {
                    page,
                    page_size,
                    excluded_statuses,
                    booking_manager_id,
                    campaign_id,
                    influencer_id,
                    client_id,
                    booker_id,
                    date_created_start,
                    date_created_end,
                    date_approved_start,
                    date_approved_end,
                },
                requestInit
            );
        },
        [
            excluded_statuses,
            fetchBookingsData,
            page,
            page_size,
            booking_manager_id,
            campaign_id,
            influencer_id,
            client_id,
            booker_id,
            date_created_start,
            date_created_end,
            date_approved_start,
            date_approved_end,
        ]
    );

    const reset = useCallback(() => {
        setBookings([]);
        setTotals(null);
        setInfluencers([]);
        setPage(1);
        setHasNextPage(false);
        setIsInitialized(false);
        setIsLoading(false);
        setHasErrorLoadingData(false);
    }, []);

    const loadNextPage = useCallback(
        async (requestInit?: RequestInit) => {
            await fetchBookingsData(
                {
                    page: page + 1,
                    page_size,
                    excluded_statuses,
                    booking_manager_id,
                    campaign_id,
                    influencer_id,
                    client_id,
                    booker_id,
                    date_created_start,
                    date_created_end,
                    date_approved_start,
                    date_approved_end,
                },
                requestInit
            );
            setPage((page) => page + 1);
        },
        [
            excluded_statuses,
            fetchBookingsData,
            page,
            page_size,
            booking_manager_id,
            campaign_id,
            influencer_id,
            client_id,
            booker_id,
            date_created_start,
            date_created_end,
            date_approved_start,
            date_approved_end,
        ]
    );

    const debouncedLoadNextPage = useMemo(
        () => debounce(loadNextPage, 500, { leading: true, trailing: false }),
        [loadNextPage]
    );

    const addBooking: UseAdminBookingsResult['addBooking'] = useCallback(async (bookingId) => {
        const response = await getAdminBooking(bookingId);
        if (response.status === 200) {
            setBookings((bookings) => [response.data, ...bookings]);
            return { success: true };
        }

        return { success: false, reason: 'Booking not found' };
    }, []);

    const refreshBooking = useCallback(async (bookingId: number) => {
        const response = await getAdminBooking(bookingId);
        if (response.status === 404) {
            return {
                success: false,
                reason: 'Booking not found',
            };
        }

        setBookings((bookings) =>
            bookings.map((booking) => (booking.id === response.data.id ? response.data : booking))
        );
        return { success: true };
    }, []);

    const editBooking: UseAdminBookingsResult['editBooking'] = useCallback(
        async (bookingId, data) => {
            const response = await patchAdminBooking(bookingId, data);
            if (response.status !== 200) {
                const errorMessage =
                    response.status === 404
                        ? response.data.detail
                        : Object.values(mapApiErrorsToFormikErrors(response.data))[0] || 'Could not update booking';
                return { success: false, reason: errorMessage };
            }

            const refreshResult = await refreshBooking(bookingId);
            if (!refreshResult.success) {
                return { success: false, reason: 'Booking updated, but could not refresh data' };
            }

            return { success: true };
        },
        [refreshBooking]
    );

    const setStatus: UseAdminBookingsResult['setStatus'] = useCallback(
        async (bookingId, status) => {
            const statusOperations: {
                [key in BookingStatus]?: (bookingId: number) => Promise<ApiResponse<any>>;
            } = {
                confirmed: confirmAdminBooking,
                declined: declineAdminBooking,
                draft_approved: (bookingId: number) => submitAdminBookingStatus(bookingId, 'draft_approved'),
                draft_sent: (bookingId: number) => submitAdminBookingStatus(bookingId, 'draft_sent'),
                content_only: (bookingId: number) => submitAdminBookingStatus(bookingId, 'content_only'),
                redrafting: (bookingId: number) => submitAdminBookingStatus(bookingId, 'redrafting'),
                voided: (bookingId: number) => submitAdminBookingStatus(bookingId, 'voided'),
            };

            const operation = statusOperations[status];
            if (!operation) {
                return { success: false, reason: 'Operation is not supported' };
            }

            const response = await operation(bookingId);
            if (response.status >= 300) {
                return { success: false, reason: 'Could not update status' };
            }

            const refreshResults = await refreshBooking(bookingId);
            if (!refreshResults.success) {
                return {
                    success: false,
                    reason: 'Status updated, but could not refresh booking data',
                };
            }

            return { success: true };
        },
        [refreshBooking]
    );

    const approveToBill: UseAdminBookingsResult['approveToBill'] = useCallback(
        async (bookingId, data) => {
            const response = await approveAdminBookingToBill(bookingId, data);
            if (response.status === 404) {
                return { success: false, reason: 'Booking not found' };
            }

            const refreshResult = await refreshBooking(bookingId);
            if (!refreshResult.success) {
                return { success: false, reason: 'Approved to bill, but could not refresh booking data' };
            }

            return { success: true };
        },
        [refreshBooking]
    );

    const createAndSendInvoice = useCallback(async (data: CreateAndSendAdminInvoiceData) => {
        const response = await createAndSendAdminInvoice(data);

        if (response.status === 201) {
            return { success: true };
        }

        if (response.status === 400) {
            const errorMessage = Array.isArray(response.data) ? response.data.join(', ') : response.data;
            return { success: false, reason: errorMessage };
        }

        return { success: false, reason: "Couldn't create and send invoice" };
    }, []);

    return useMemo(
        () => ({
            bookings,
            influencers,
            totals,
            isLoading,
            hasErrorLoadingData,
            hasNextPage,
            init,
            reset,
            loadNextPage: debouncedLoadNextPage,
            isInitialized,
            setStatus,
            approveToBill,
            createAndSendInvoice,
            addBooking,
            editBooking,
        }),
        [
            addBooking,
            approveToBill,
            createAndSendInvoice,
            bookings,
            influencers,
            editBooking,
            hasErrorLoadingData,
            hasNextPage,
            init,
            reset,
            isInitialized,
            isLoading,
            debouncedLoadNextPage,
            setStatus,
            totals,
        ]
    );
}
